import { yupResolver } from '@hookform/resolvers/yup'
import { findIndex, map } from 'lodash-es'
import { useEffect, useState } from 'react'
import { useFieldArray, useForm } from 'react-hook-form'
import { useParams } from 'react-router-dom'
import * as yup from 'yup'

import { SettingsGender, Languages } from '../../../../common/models'
import FormGroup from '../../../../core/components/FormGroup'
import { GENDERS } from '../../../../core/constants'
import {
  deleteSettingsGender,
  getSettingsGender,
  updateSettingsGender,
} from '../../../../core/services/festival-settings'
import { DeleteConfirmationModal } from '../../DeleteConfirmationModal'
import { MessageBox } from '../../messages/MessageBox'
import { Button, Card, Input } from '@/components/ui'
import { Icon } from '@/common/components/Icon'

export interface IGendersFormValues {
  genders?: {
    id?: string
    name: string
    translations: {
      id?: string
      name: string
      locale: string
    }[]
  }[]
}

interface GenderProps {
  allLanguages: Languages[]
  hasEditPermission: boolean
}

export const Genders: React.FC<GenderProps> = (props: GenderProps) => {
  const { allLanguages, hasEditPermission } = props
  const { editionId, festivalName } = useParams()
  const [
    showDeleteConfirmationModalGenders,
    setDeleteConfirmationModalGenders,
  ] = useState<boolean>(false)
  const [selectedGenderIndex, setSelectedGenderIndex] = useState<
    number | undefined
  >()
  const [genders, setGenders] = useState<SettingsGender[]>()

  const [isError, setIsError] = useState<boolean>(false)
  const [errorStatus, setErrorStatus] = useState<number | undefined>(undefined)
  const [showMessageBox, setShowMessageBox] = useState<boolean>(false)
  const [message, setMessage] = useState<string>('')

  const defaultLanguageContent = map(allLanguages, (lang) => {
    return {
      locale: lang.locale,
      name: '',
    }
  })

  useEffect(() => {
    // prettier-ignore
    (async () => {
      try {
        const resp = await getSettingsGender()
        if (resp.status === 200) {
          setGenders(resp.data)
          setTimeout(() => {
            setValue('genders', resp.data)
          }, 200)
        }
      } catch (e) {
        if (e instanceof Error) {
          setIsError(true)
          setShowMessageBox(true)
          setMessage(e.message)
          setErrorStatus((e as { response?: { status?: number } })?.response?.status)
        } else {
          console.error(e)
        }
      }
    })()
  }, [editionId, festivalName, allLanguages])

  const schema = yup.object().shape({
    genders: yup
      .array()
      .of(
        yup.object().shape({
          id: yup.string().optional(),
          name: yup.string().required('Field is required'),
          translations: yup
            .array()
            .of(
              yup.object().shape({
                id: yup.string().optional(),
                locale: yup.string().required('Field is required'),
                name: yup.string().required('Field is required'),
              }),
            )
            .required(),
        }),
      )
      .required(),
  })

  const {
    handleSubmit,
    register,
    watch,
    setValue,
    formState: { errors, isSubmitting },
    control,
  } = useForm<IGendersFormValues>({
    mode: 'onChange',
    resolver: yupResolver<IGendersFormValues>(schema),
    defaultValues: {},
  })

  const {
    fields: gendersFields,
    append: appendGenders,
    remove: removeGenders,
  } = useFieldArray({
    control: control,
    name: 'genders',
  })

  const gendersWatch = watch('genders')

  const submit = async (values: IGendersFormValues) => {
    try {
      if (values?.genders) {
        const { status } = await updateSettingsGender(values.genders)
        if (status === 200) {
          setIsError(false)
          setShowMessageBox(true)
          setMessage('Update Successful')
        }
      }
    } catch (e) {
      if (e instanceof Error) {
        setIsError(true)
        setShowMessageBox(true)
        setMessage(e.message)
        setErrorStatus(
          (e as { response?: { status?: number } })?.response?.status,
        )
      } else {
        console.error(e)
      }
    }
  }

  const handleCancel = async () => {
    const temp = map(genders, (gender) => {
      const transTemp = defaultLanguageContent
      map(gender.translations, (trans) => {
        const index = findIndex(allLanguages, (l) => l.locale === trans.locale)
        if (index !== -1) {
          transTemp[index].locale = trans.locale
          transTemp[index].name = trans.name
        }
      })
      return {
        id: gender.id,
        name: gender.name,
        translations: transTemp,
      }
    })
    setTimeout(() => {
      setValue('genders', temp)
    }, 200)
  }

  const deleteGender = async () => {
    try {
      if (selectedGenderIndex) {
        removeGenders(selectedGenderIndex)
        if (gendersWatch?.[selectedGenderIndex]?.id) {
          const resp = await deleteSettingsGender(
            gendersWatch?.[selectedGenderIndex]?.id || '',
          )
          if (resp.status === 204) {
            setIsError(false)
            setShowMessageBox(true)
            setMessage('Delete Translations Successful')
          }
        }
      }
    } catch (e) {
      if (e instanceof Error) {
        setIsError(true)
        setShowMessageBox(true)
        setMessage(e.message)
        setErrorStatus(
          (e as { response?: { status?: number } })?.response?.status,
        )
      } else {
        console.error(e)
      }
    }
  }

  return (
    <>
      <Card className="border border-monochrome-120 bg-gradient-4 p-7.5">
        <h3 className="text-title-xs text-monochrome-1 mb-6">Genders</h3>

        <form
          id="settings-gender-form"
          className="space-y-6"
          onSubmit={handleSubmit(submit)}
        >
          {map(gendersFields, (field, index) => (
            <Card
              className="w-full p-4 rounded-2xl bg-monochrome-3-60 space-y-4"
              key={field.id}
            >
              <div className="flex flex-col gap-3">
                {map(allLanguages, (language, i) => {
                  return (
                    <div className="grid grid-cols-2 gap-4" key={language.id}>
                      <FormGroup label="Gender Name" wrapperClassName="mb-0">
                        <Input
                          isInvalid={
                            !!errors?.genders?.[index]?.translations?.[i]?.name
                          }
                          {...register(
                            `genders.${index}.translations.${i}.name`,
                          )}
                          onChange={(e) => {
                            setValue(
                              `genders.${index}.translations.${i}.locale`,
                              language.locale,
                            )
                          }}
                          type="text"
                          disabled={!hasEditPermission}
                        />
                      </FormGroup>
                      <FormGroup
                        label="Language"
                        direction="column"
                        wrapperClassName="mb-0"
                      >
                        <Input
                          value={language.name}
                          readOnly
                          className="bg-monochrome-120"
                        />
                      </FormGroup>
                    </div>
                  )
                })}
              </div>
              <div className="flex justify-end">
                <Button
                  variant="destructive"
                  type="button"
                  disabled={!hasEditPermission}
                  onClick={() => {
                    setDeleteConfirmationModalGenders(true)
                    setSelectedGenderIndex(index)
                  }}
                >
                  Delete
                  <Icon
                    icon="close-circle"
                    width="18"
                    height="18"
                    viewBox="0 0 18 18"
                    fill="none"
                    className="shrink-0"
                  />
                </Button>
              </div>
            </Card>
          ))}
          <div className="flex justify-center">
            <Button
              onClick={() => {
                if (gendersWatch?.length) {
                  const rest = GENDERS.filter((one) => {
                    let isSelected = false
                    gendersWatch.forEach((gender) => {
                      if (gender.name === one) {
                        isSelected = true
                      }
                    })
                    if (!isSelected) {
                      return one
                    }
                  })
                  appendGenders({
                    name: rest[0],
                    translations: [],
                  })
                } else {
                  appendGenders({
                    name: GENDERS[0],
                    translations: [],
                  })
                }
              }}
              disabled={!hasEditPermission}
              type="button"
              variant="secondary"
            >
              Add New Gender
              <Icon
                icon="plus"
                width="18"
                height="18"
                viewBox="0 0 18 18"
                fill="none"
                className="shrink-0"
              />
            </Button>
          </div>
        </form>

        <div className="flex justify-end gap-2 p-3 border-t border-monochrome-120 pt-4 [&>*]:w-[154px] mt-4">
          <Button
            onClick={handleCancel}
            variant="secondary"
            disabled={!hasEditPermission}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            form="settings-gender-form"
            type="submit"
            disabled={isSubmitting || !hasEditPermission}
            loading={isSubmitting}
          >
            Save
          </Button>
        </div>
      </Card>
      <MessageBox
        message={message}
        setShowMessageBox={setShowMessageBox}
        showMessageBox={showMessageBox}
        isError={isError}
        errorStatus={errorStatus}
      />
      <DeleteConfirmationModal
        showDeleteConfirmationModal={showDeleteConfirmationModalGenders}
        setDeleteConfirmationModal={setDeleteConfirmationModalGenders}
        deleteTitle={'Genders'}
        size="md"
        deleteFunc={deleteGender}
        onClose={() => setSelectedGenderIndex(undefined)}
      />
    </>
  )
}
